<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { until } from '@vueuse/core'
import { get as _get } from 'lodash-es'
import { type GeoJSONSource, type Map } from 'maplibre-gl'
import { Feature, FeatureCollection, Point, Polygon } from 'geojson'
import ky from 'ky'
import { featureCollection, point } from '@turf/turf'
import { feature as topojsonToGeojson } from 'topojson-client'

import BasicMap from './BasicMap.vue'
import AnalyticsPopup from './AnalyticsPopup.vue'
import AnalyticsPopupBusiness from './AnalyticsPopupBusiness.vue'
import {
  createSpatialIndex,
  addPointPropertiesToFeatureCollection,
  SpatialIndex,
} from '@/utilities/geometry'
import { VaryingPropertyTypes } from '@/stores/properties'
import { VaryingReportTypes } from '@/composables/analytics'
import { IntensityCommodity } from '@/utilities/reportsIntensity'
import { CountOfCompleteReportsEntry } from '@/utilities/geo'

import IntensitySelector from '@/components/IntensitySelector.vue'
import { EmissionsReportT } from '@ruminati/types_v2/emissions-reports'

const props = defineProps<{
  properties: VaryingPropertyTypes[]
  reports: VaryingReportTypes[]
  relevantIntensityMetrics: IntensityCommodity[]
}>()

type BaseDisplayTypeConfig = {
  label: string
  value: string
}

type RegionDisplayTypeConfig = BaseDisplayTypeConfig & {
  path: string
  data?: AggregationPolygons
  spatialIndex?: SpatialIndex
}

type PointsDisplayTypeConfig = BaseDisplayTypeConfig & {
  data?: PropertyWithReportPropertiesAsPoints
}

type DisplayTypeConfig = RegionDisplayTypeConfig | PointsDisplayTypeConfig

const dataDisplayTypeOptions: DisplayTypeConfig[] = [
  { label: '250km Grid', value: '250km', path: '/map-data/250km_grid.geojson', data: undefined },
  { label: '100km Grid', value: '100km', path: '/map-data/100km_grid.geojson', data: undefined },
  { label: '50km Grid', value: '50km', path: '/map-data/50km_grid.geojson', data: undefined },
  {
    label: 'Rainfall Regions',
    value: 'rainfallRegions',
    path: '/map-data/rainfallRegions.geojson',
    data: undefined,
  },
  {
    label: 'ABARES Regions',
    value: 'abares',
    path: '/map-data/abares_regions.geojson',
    data: undefined,
  },
  { label: 'SA2 Regions', value: 'sa2', path: '/map-data/SA2.topojson', data: undefined },
  { label: 'States', value: 'states', path: '/map-data/states.geojson', data: undefined },
  { label: 'As Approx Locations', value: 'points', data: undefined },
]
const selectedDisplayTypeId = ref(dataDisplayTypeOptions[0].value)

const selectedDisplayDataset = computed<DisplayTypeConfig>(() => {
  return dataDisplayTypeOptions.find((o) => o.value === selectedDisplayTypeId.value)!
})

const selectedSymbologyTypeId = ref('')
let mapboxMap = null as null | Map

onMounted(async () => {
  await until(
    () => props.relevantIntensityMetrics && props.relevantIntensityMetrics.length > 0,
  ).toBe(true)
  if (props.relevantIntensityMetrics && props.relevantIntensityMetrics?.length > 0) {
    const metric =
      props.relevantIntensityMetrics.findIndex((m) => m.value === 'beef') > -1
        ? 'beef'
        : props.relevantIntensityMetrics?.[0].value
    selectedSymbologyTypeId.value = metric
  }
})

// Note a variant of these will be used
// in AnalyticsPopup, although the nested things will be stringified
// Any changes made here should be made there too
export type AggregationProperties = {
  propertyIds: string[]
  propertyNames: string[]
  reportIds: (string | null)[]
  symbologyField?: number
  allValues: number[]
}
export type AggregationPolygons = FeatureCollection<Polygon, AggregationProperties>

async function mapReady(map: Map) {
  mapboxMap = map

  map.addSource('grid-src', {
    type: 'geojson',
    promoteId: 'id',
    data:
      selectedDisplayDataset.value.data && selectedDisplayDataset.value.data.features.length > 0
        ? selectedDisplayDataset.value.data
        : { type: 'FeatureCollection', features: [] },
  })

  map.addLayer({
    id: 'grid-lyr',
    type: 'fill',
    source: 'grid-src',
    filter: ['==', '$type', 'Polygon'],
    layout: {},
    // @ts-expect-error strange typing
    paint: fillStyle.value,
  })

  map.addLayer({
    id: 'grid-line-lyr',
    type: 'line',
    source: 'grid-src',
    filter: ['==', '$type', 'Polygon'],
    layout: {},
    paint: {
      'line-color': '#6e6e6e',
      'line-width': 1,
      'line-opacity': 0.11,
    },
  })

  map.addSource('business-centroid-src', {
    type: 'geojson',
    promoteId: 'id',
    data: { type: 'FeatureCollection', features: [] },
  })

  map.addLayer({
    id: 'business-centroid-src-circle-lyr',
    type: 'circle',
    source: 'business-centroid-src',
    filter: ['!=', ['get', 'symbologyResult'], null],
    layout: {
      visibility: 'none',
      // 'circle-sort-key': ['*', ['get', this.selectedMetric?.pointSymbologyName as string], -1]
    },
    // @ts-expect-error strange typing
    paint: circleStyle.value,
  })

  updateMapData()
}

const selectedSymbologyTypeDetails = computed(() => {
  if (selectedSymbologyTypeId.value === 'report-complete-count') {
    return CountOfCompleteReportsEntry
  }
  return props.relevantIntensityMetrics.find((ic) => ic.value === selectedSymbologyTypeId.value)
})

const fillStyle = computed(() => {
  return {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'symbologyField'], 0],
      minMaxRange.value[0],
      '#FDEA6F',
      minMaxRange.value[1],
      '#8B412B',
    ],
    'fill-opacity': ['case', ['>', ['coalesce', ['get', 'symbologyField'], 0], 0], 0.8, 0.0],
  }
})

const circleStyle = computed(() => {
  return {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['get', 'symbologyResult'],
      minMaxRange.value[0],
      10,
      minMaxRange.value[1],
      20,
    ],
    'circle-color': [
      'interpolate',
      ['linear'],
      ['get', 'symbologyResult'],
      minMaxRange.value[0],
      '#FDEA6F',
      minMaxRange.value[1],
      '#8B412B',
    ],
    'circle-opacity': ['case', ['!=', ['get', 'symbologyResult'], null], 0.8, 0.1],
    'circle-stroke-width': 2,
    'circle-stroke-color': 'white',
  }
})

const minMaxRange = computed<[number, number]>(() => {
  if (!selectedSymbologyTypeDetails.value) return [0, 100]
  if (selectedSymbologyTypeDetails.value.value === 'report-complete-count') {
    const vals = displayPolygons.value
      ? (displayPolygons.value.features
          .map((f) => f.properties.symbologyField)
          .filter((v) => v) as number[])
      : []
    return [1, Math.max(...vals)]
  } else if ('intensityReference' in selectedSymbologyTypeDetails.value) {
    return [
      (selectedSymbologyTypeDetails.value.intensityReference?.nationalAverage ?? 2) / 2,
      (selectedSymbologyTypeDetails.value.intensityReference?.nationalAverage ?? 2) * 2,
    ]
  }
  return [0, 100]
})

export type PropertyWithReportProperties = {
  propertyId: string
  propertyName: string
  reportId: string | undefined
  summaryResults: EmissionsReportT['results'] | undefined
  symbologyResult: number | undefined | null
}
export type PropertyWithReportPropertiesAsPoints = FeatureCollection<
  Point,
  PropertyWithReportProperties
>

const reportDataAsPoints = computed<PropertyWithReportPropertiesAsPoints>(() => {
  const features: Feature<Point, PropertyWithReportProperties>[] = []

  props.properties.forEach((p) => {
    if (p.centroid) {
      const report = props.reports?.find((rlf) => rlf.propertyId === p.id)

      let symbologyValue: number | null = null

      if (selectedSymbologyTypeDetails.value && report) {
        if (selectedSymbologyTypeDetails.value?.value === 'report-complete-count') {
          if (report.completionDate) symbologyValue = 1
        } else if (
          selectedSymbologyTypeDetails.value &&
          'cropIntensity' in selectedSymbologyTypeDetails.value
        ) {
          const crop = selectedSymbologyTypeDetails.value.findFirstCropInReport(report) ?? null
          if (crop)
            symbologyValue =
              selectedSymbologyTypeDetails.value.getCommodityIntensity(report, crop) ?? null
        } else if ('getCommodityIntensity' in selectedSymbologyTypeDetails.value) {
          symbologyValue = selectedSymbologyTypeDetails.value.getCommodityIntensity(report) ?? null
        }
      }

      features.push(
        point(p.centroid, {
          propertyId: p.id,
          propertyName: p.name,
          reportId: report?.id,
          summaryResults: report?.results ?? undefined,
          symbologyResult: symbologyValue,
        }),
      )
    }
  })
  return featureCollection(features)
})

const displayPolygons = computed<AggregationPolygons | undefined>(() => {
  // If the point dataset is selected then return nothing
  if (!('path' in selectedDisplayDataset.value) || !selectedDisplayDataset.value.spatialIndex)
    return undefined

  const clonedPolys = structuredClone(selectedDisplayDataset.value.data) as AggregationPolygons
  const out = addPointPropertiesToFeatureCollection({
    polyFC: clonedPolys,
    polygonSpatialIndex: selectedDisplayDataset.value.spatialIndex,
    pointFC: reportDataAsPoints.value,
    symbologyMethod:
      selectedSymbologyTypeDetails.value?.value === 'report-complete-count' ? 'count' : undefined,
  })
  return out
})

function updateMapData() {
  let srcId: string | null = null
  let srcData: FeatureCollection | null = null
  if ('path' in selectedDisplayDataset.value && displayPolygons.value) {
    srcId = 'grid-src'
    srcData = displayPolygons.value
  } else if (!('path' in selectedDisplayDataset.value)) {
    srcId = 'business-centroid-src'
    srcData = reportDataAsPoints.value
  }
  if (mapboxMap && srcId && srcData) {
    const src = mapboxMap.getSource(srcId) as GeoJSONSource
    src.setData(srcData)
  }
}

watch(
  () => props.reports,
  () => {
    updateDataAndSymbology()
  },
)

watch(selectedSymbologyTypeId, () => {
  updateDataAndSymbology()
})

watch(
  selectedDisplayTypeId,
  async () => {
    if (
      selectedDisplayDataset.value &&
      'path' in selectedDisplayDataset.value &&
      !selectedDisplayDataset.value.data
    ) {
      selectedDisplayDataset.value.data = await loadAggregationRegions(
        selectedDisplayDataset.value.path,
      )
      if (!selectedDisplayDataset.value.spatialIndex) {
        selectedDisplayDataset.value.spatialIndex = createSpatialIndex(
          selectedDisplayDataset.value.data,
        )
      }
    }
    updateDataAndSymbology()
  },
  { immediate: true },
)

function updateDataAndSymbology() {
  if (mapboxMap) {
    updateMapData()

    if ('path' in selectedDisplayDataset.value) {
      setPolygonSymbology()
    } else {
      setPointSymbology()
    }
  }
}

function setPointSymbology() {
  if (!mapboxMap) return
  mapboxMap.setLayoutProperty('business-centroid-src-circle-lyr', 'visibility', 'visible')
  mapboxMap.setLayoutProperty('grid-lyr', 'visibility', 'none')
  mapboxMap.setLayoutProperty('grid-line-lyr', 'visibility', 'none')

  Object.entries(circleStyle.value).forEach(([key, value]) => {
    if (mapboxMap && mapboxMap.getLayer('business-centroid-src-circle-lyr'))
      mapboxMap.setPaintProperty('business-centroid-src-circle-lyr', key, value)
  })
}

function setPolygonSymbology() {
  if (!mapboxMap) return
  mapboxMap.setLayoutProperty('business-centroid-src-circle-lyr', 'visibility', 'none')
  mapboxMap.setLayoutProperty('grid-lyr', 'visibility', 'visible')
  mapboxMap.setLayoutProperty('grid-line-lyr', 'visibility', 'visible')
  Object.entries(fillStyle.value).forEach(([key, value]) => {
    if (mapboxMap && mapboxMap.getLayer('grid-lyr'))
      mapboxMap.setPaintProperty('grid-lyr', key, value)
  })
}

async function loadAggregationRegions(path: string): Promise<AggregationPolygons> {
  let data = await ky.get(path).json<AggregationPolygons>()
  if (path.includes('.topojson')) {
    const typedData = data as any
    const allFeatures: Feature<Polygon>[] = []
    Object.keys(typedData.objects).forEach((objectName) => {
      const geoJson = topojsonToGeojson(
        typedData,
        typedData.objects[objectName],
      ) as unknown as FeatureCollection<Polygon, AggregationPolygons>
      allFeatures.push(...geoJson.features)
    })
    data = featureCollection(allFeatures) as AggregationPolygons
  }

  // Add some core things to the polys
  data.features.forEach((f) => {
    const aggregationProps: AggregationProperties = {
      propertyIds: [],
      propertyNames: [],
      reportIds: [],
      allValues: [],
    }
    f.properties = {
      ...f.properties,
      ...aggregationProps,
    }
  })
  return data
}
</script>

<template>
  <a-flex>
    <a-flex vertical class="w-full">
      <a-flex style="position: relative">
        <BasicMap
          id="analytics-map"
          height="450px"
          :bounds="[
            [108.91, -45.59],
            [157.64, -6.89],
          ]"
          :hide-border="true"
          :padding-on-bounds="1"
          basemap="grey"
          @map-ready="mapReady"
        >
          <AnalyticsPopup
            v-if="selectedDisplayDataset.value !== 'points'"
            :selected-metric="selectedSymbologyTypeId"
          />
          <AnalyticsPopupBusiness
            v-if="selectedDisplayDataset.value === 'points'"
            :selected-metric="selectedSymbologyTypeId"
          />
        </BasicMap>
        <div id="legend" class="border-rd-2 b-solid b-2 b-rumi-grey-500 p-2">
          <a-flex>
            <a-flex vertical class="w-full">
              <a-flex>
                <p class="text-rumi-grey-600 font-500 mb-1 text-sm tracking-wider">
                  {{ selectedSymbologyTypeDetails?.label }}
                </p>
              </a-flex>
              <a-flex justify="space-between" class="mb-1">
                <p class="text-sm">
                  <span v-if="selectedSymbologyTypeDetails?.value !== 'report-complete-count'">
                    &lt;{{ minMaxRange[0] }}kg
                  </span>
                  <span v-else>{{ minMaxRange[0] }}</span>
                </p>
                <p class="text-sm">
                  <span v-if="selectedSymbologyTypeDetails?.value !== 'report-complete-count'">
                    &gt;{{ minMaxRange[1] }}kg
                  </span>
                  <span v-else>{{ minMaxRange[1] }}</span>
                </p>
              </a-flex>
              <a-flex>
                <div id="legendSwatch" />
              </a-flex>
            </a-flex>
          </a-flex>
        </div>
      </a-flex>
      <a-flex justify="space-between" class="mt-4">
        <a-select
          v-model:value="selectedDisplayTypeId"
          :options="dataDisplayTypeOptions"
          style="width: 200px"
        />

        <IntensitySelector
          :selected-metric-id="selectedSymbologyTypeId"
          :on-select="(key) => (selectedSymbologyTypeId = key)"
          :prefix-extra-items="[
            {
              type: 'group',
              label: 'MISC',
              children: [CountOfCompleteReportsEntry],
            },
          ]"
        />
      </a-flex>
    </a-flex>
  </a-flex>
</template>

<style lang="scss">
#legend {
  position: absolute;
  display: inline-block;
  bottom: 20px;
  left: 20px;

  background: white;

  #legendSwatch {
    height: 10px;
    width: 200px;
    background-image: linear-gradient(to right, #fdea6f, #8b412b);
  }
}
</style>
